/**
 * Console → docs URL helpers.
 *
 * Call sites should prefer `docsLinkUrl(id)` for registered deep links so CI can
 * verify those pages exist. Use `docsUrl(slug)` only when building a URL from a
 * known slug (the registry entries call through this).
 */
import { getSelectedDC } from './config'
import { CONSOLE_DOCS_LINKS, listConsoleDocsLinks } from './docsLinks.registry'

export { CONSOLE_DOCS_LINKS, listConsoleDocsLinks }
export type { ConsoleDocsLink } from './docsLinks.registry'

export type ConsoleDocsLinkId = keyof typeof CONSOLE_DOCS_LINKS

const trimTrailingSlash = (s: string) => s.replace(/\/+$/, '')

/** Docs site origin for the current build / environment. */
export function getDocsSiteOrigin(): string {
  const explicit =
    typeof import.meta.env.VITE_DOCS_URL === 'string' ? import.meta.env.VITE_DOCS_URL.trim() : ''
  if (explicit) return trimTrailingSlash(explicit)
  return 'https://docs.veilstream.com'
}

export type DocsUrlOptions = {
  /** Override data-center region query (defaults to the selected DC in the browser). */
  region?: string | null
  /** When false, omit the region query param. */
  includeRegion?: boolean
}

/**
 * Build an absolute docs URL from a path slug after `/docs/`.
 * Pass `''` for the docs home page.
 */
export function docsUrl(slug: string, options: DocsUrlOptions = {}): string {
  const trimmed = slug.replace(/^\/+|\/+$/g, '')
  const base = getDocsSiteOrigin()
  const path = trimmed === '' ? `${base}/` : `${base}/docs/${trimmed}`

  const includeRegion = options.includeRegion !== false
  let region = options.region
  if (includeRegion && region === undefined && typeof window !== 'undefined') {
    region = getSelectedDC()
  }
  if (!includeRegion || region == null || region === '') {
    return path
  }

  const url = new URL(path, base)
  url.searchParams.set('region', region)
  return url.toString()
}

/** Build an absolute docs URL for a registered console docs link. */
export function docsLinkUrl(id: ConsoleDocsLinkId, options?: DocsUrlOptions): string {
  const link = CONSOLE_DOCS_LINKS[id]
  if (!link) {
    throw new Error(`Unknown console docs link id: ${String(id)}`)
  }
  return docsUrl(link.slug, options)
}
